import { FC, memo, useCallback } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { CompanyDetailSaleCaseMapPin, CompanyDetailSaleCasesMapBounds } from '@hooks_iqfu/dashboard/useSaleCase';

import { SaleCaseItem } from '@type-def_iqfu/public/SaleCaseItem';

import { PropertyType } from '@constants_iqfu';

import styles from './Map.module.scss';

// @ts-ignore
import mapboxgl from '!mapbox-gl';

import 'mapbox-gl/dist/mapbox-gl.css';

mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOX_CONSOLE_TOKEN;

type CommonProps = {
  pageKey: string;
  lat: number;
  long: number;
  zoom: number;
  fitBounds?: boolean;
  showCityName?: boolean;
};

type CompanyDetailProps = CommonProps & {
  mapType: 'company_detail';
  mapBounds: CompanyDetailSaleCasesMapBounds | undefined;
  companyDetailSaleCasesMapPinList: CompanyDetailSaleCaseMapPin[];
  companyLocation: {
    long: number;
    lat: number;
  };
};

type PricePageProps = CommonProps & {
  mapType: 'price_page';
  saleCaseMapPinList: SaleCaseItem[];
};

type Props = CompanyDetailProps | PricePageProps;

type pinData = SaleCaseItem | CompanyDetailSaleCaseMapPin;

const SaleCasesMap: FC<Props> = (props) => {
  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <SaleCasesMapContent {...props} />
    </ErrorBoundary>
  );
};

const SaleCasesMapContent: FC<Props> = memo((props) => {
  const { lat, long, zoom, showCityName, fitBounds, mapType } = props;

  const renderData =
    mapType === 'price_page'
      ? (props as PricePageProps).saleCaseMapPinList
      : (props as CompanyDetailProps).companyDetailSaleCasesMapPinList;

  const getPopupContentsText = (
    propertyType: keyof typeof PropertyType,
    saleCase: SaleCaseItem | CompanyDetailSaleCaseMapPin,
  ) => {
    if (propertyType === 'mansion') {
      return saleCase.mansion_name;
    } else if (showCityName) {
      return saleCase.city.name + ' ' + saleCase.town.name;
    } else {
      return saleCase.town.name;
    }
  };

  const createPinElement = (icon: 'mansion' | 'land' | 'used_house' | 'new_house' | 'default_pin') => {
    const pinIcon = document.createElement('div');
    if (icon === 'mansion') {
      pinIcon.className = 'property_icon';
      pinIcon.style.backgroundImage = `url('/images/iqfu/public/map/sale-case-flag-mansion.svg')`;
    } else if (icon === 'land') {
      pinIcon.className = 'property_icon';
      pinIcon.style.backgroundImage = `url('/images/iqfu/public/map/sale-case-flag-land.svg')`;
    } else {
      pinIcon.className = 'property_icon';
      pinIcon.style.backgroundImage = `url('/images/iqfu/public/map/sale-case-flag-house.svg')`;
    }

    if (icon === 'default_pin') {
      pinIcon.style.backgroundImage = `url('/images/iqfu/public/map/pin-icon-default.svg')`;
      pinIcon.style.width = '30px';
    }
    pinIcon.style.backgroundSize = 'contain';
    pinIcon.style.backgroundRepeat = 'no-repeat';

    return pinIcon;
  };

  const popUpOptions = { offset: 10, closeButton: false, className: 'popup_container' };

  const renderMapCallback = useCallback(
    (node: HTMLDivElement) => {
      if (node) {
        const map = new mapboxgl.Map({
          container: node,
          style: 'mapbox://styles/iqradev/ckyz519l5001815l0zo007xzd',
          center: [long, lat],
          zoom: zoom,
          minZoom: 4,
          cooperativeGestures: true,
          locale: {
            'TouchPanBlocker.Message': '地図を移動するには、2本指でドラッグしてください。',
          },
        });

        map.addControl(new mapboxgl.NavigationControl({ interactive: false, showCompass: false }), 'top-left');
        map.addControl(new mapboxgl.FullscreenControl(), 'top-left');
        map.scrollZoom.disable(); //スクロールで拡大縮小禁止
        map.doubleClickZoom.disable(); //ダブルクリックで拡大禁止
        map.dragRotate.disable(); //回転禁止
        map.touchZoomRotate.disableRotation(); //回転禁止

        if (mapType === 'price_page' && fitBounds && renderData && renderData.length > 1) {
          //※中心座標から+-3以上離れているデータはピンの表示エリア対象外にする
          const filteredData = (renderData as Array<SaleCaseItem | CompanyDetailSaleCaseMapPin>).filter(
            (pin) => !(Math.abs(pin.lat - lat) > 3 || Math.abs(pin.long - long) > 3),
          );
          const lats = filteredData.map((pin) => pin.lat);
          const longs = filteredData.map((pin) => pin.long);

          const minLat = Math.min(...lats);
          const maxLat = Math.max(...lats);
          const minLong = Math.min(...longs);
          const maxLong = Math.max(...longs);
          if (filteredData.length > 2) {
            map.fitBounds(
              [
                [minLong, minLat], // southwestern corner of the bounds
                [maxLong, maxLat], // northeastern corner of the bounds,
              ],
              { duration: 0, padding: 35 }, // disable animation
            );
          }
        }

        if (
          mapType === 'company_detail' &&
          fitBounds &&
          //mapBoundsがない場合と値がnullの場合を除外する
          !!props.mapBounds?.min_long &&
          renderData &&
          renderData.length > 0
        ) {
          map.fitBounds(
            [
              [props.mapBounds.min_long, props.mapBounds.min_lat], // southwestern corner of the bounds
              [props.mapBounds.max_long, props.mapBounds.max_lat], // northeastern corner of the bounds,
            ],
            { duration: 0, padding: 35 }, // disable animation
          );
        }

        if (renderData) {
          combineSameLocationPins(renderData).map((pinData) => {
            const pinElement = createPinElement(pinData.property_type);

            // 複数成約している場合、その数字をpinの左上に出す。
            if (pinData.duplicateCount !== 1) {
              const dealNumberElement = document.createElement('div');
              // 物件種別に応じて、styleを変更する
              dealNumberElement.className = styles[`deals_number_${pinData.property_type}`];
              // 成約数を表示する
              dealNumberElement.innerHTML = pinData.duplicateCount.toString();
              pinElement.appendChild(dealNumberElement);
            }
            const popup = new mapboxgl.Popup({ ...popUpOptions }).setHTML(
              `<a href="${
                pinData.property_type === 'mansion'
                  ? `/mansions/${pinData.mansion_id}`
                  : `/landhouses/${pinData.town.code}`
              }" class="popup_link" href>${getPopupContentsText(pinData.property_type, pinData)}</a>`,
            );

            new mapboxgl.Marker(pinElement).setLngLat([pinData.long, pinData.lat]).setPopup(popup).addTo(map);
          });

          if (mapType === 'company_detail') {
            const companyLocationPin = createPinElement('default_pin');
            new mapboxgl.Marker(companyLocationPin)
              .setLngLat([props.companyLocation.long, props.companyLocation.lat])
              .addTo(map);
          }
        }
      }
    },
    [
      mapType === 'price_page'
        ? (props as PricePageProps).saleCaseMapPinList
        : (props as CompanyDetailProps).companyDetailSaleCasesMapPinList,
    ],
  );

  return <div ref={renderMapCallback} className={styles.container} />;
});

SaleCasesMapContent.displayName = 'SaleCasesMap';
export default SaleCasesMap;

type MapPinWithDuplicateCount =
  | (SaleCaseItem & { duplicateCount: number })
  | (CompanyDetailSaleCaseMapPin & { duplicateCount: number });

const combineSameLocationPins = (data: pinData[]): MapPinWithDuplicateCount[] => {
  const initialArray = [] as MapPinWithDuplicateCount[];

  const lists = data.reduce<MapPinWithDuplicateCount[]>((total, mapPin) => {
    const duplicateIndex = total.findIndex((item) => {
      if (mapPin.property_type === 'mansion') {
        return item.mansion_id === mapPin.mansion_id;
      } else {
        return mapPin.property_type === item.property_type && item.town.code === mapPin.town.code;
      }
    });

    if (duplicateIndex === -1) {
      return [
        ...total,
        {
          ...mapPin,
          duplicateCount: 1,
        },
      ];
    }
    if (total[duplicateIndex].duplicateCount < 99) {
      total[duplicateIndex].duplicateCount++;
    }

    return total;
  }, initialArray);
  return lists;
};

const ErrorFallback: FC = () => {
  return <div className={styles.error_display}>地図の描画でエラーが発生しました。</div>;
};
